import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { useUnsubscribeMutation, useUnsubscribeStatusQuery } from '@/hooks/useUnsubscribe';

interface UnsubscribeCardProps {
  children: ReactNode;
}

function UnsubscribeCard({ children }: UnsubscribeCardProps) {
  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl p-8 md:p-10 text-center">
        {children}
      </div>
    </div>
  );
}

interface StatusIconProps {
  isSuccess: boolean;
}

function StatusIcon({ isSuccess }: StatusIconProps) {
  return (
    <div
      className={
        'w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ' +
        (isSuccess ? 'bg-zoosuccessbg' : 'bg-zoovpalelavender')
      }
    >
      <Icon
        name={isSuccess ? 'CheckCircle' : 'MailCheck'}
        className={'text-xl ' + (isSuccess ? 'text-zoosuccesstext' : 'text-zooviolet')}
      />
    </div>
  );
}

export function UnsubscribeScreen() {
  const { t } = useTranslation('broadcasts');
  const [searchParameters] = useSearchParams();
  const tokenParameter = searchParameters.get('token');
  const token = tokenParameter === null ? '' : tokenParameter;
  const statusQuery = useUnsubscribeStatusQuery(token);
  const unsubscribeMutation = useUnsubscribeMutation(token);

  if (token.length === 0 || statusQuery.isError) {
    return (
      <UnsubscribeCard>
        <StatusIcon isSuccess={false} />
        <h1 className="text-xl font-bold text-zooindigo">{t('unsubscribe.invalidTitle')}</h1>
        <p className="text-sm text-zoomuted mt-2">{t('unsubscribe.invalidBody')}</p>
      </UnsubscribeCard>
    );
  }

  if (statusQuery.data === undefined) {
    return (
      <UnsubscribeCard>
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      </UnsubscribeCard>
    );
  }

  const brandName = statusQuery.data.brandName;
  if (statusQuery.data.isUnsubscribed) {
    return (
      <UnsubscribeCard>
        <StatusIcon isSuccess />
        <h1 className="text-xl font-bold text-zooindigo">{t('unsubscribe.doneTitle')}</h1>
        <p className="text-sm text-zoomuted mt-2">{t('unsubscribe.doneBody', { brandName })}</p>
      </UnsubscribeCard>
    );
  }

  return (
    <UnsubscribeCard>
      <StatusIcon isSuccess={false} />
      <h1 className="text-xl font-bold text-zooindigo">{t('unsubscribe.title')}</h1>
      <p className="text-sm text-zoomuted mt-2">{t('unsubscribe.body', { brandName })}</p>
      <Button
        size="large"
        className="mt-6 w-full"
        isLoading={unsubscribeMutation.isPending}
        onClick={function confirmUnsubscribe() {
          unsubscribeMutation.mutate();
        }}
      >
        {t('unsubscribe.action')}
      </Button>
      {unsubscribeMutation.isError ? (
        <p role="alert" className="mt-3 text-sm text-zooerrortext">
          {t('unsubscribe.error')}
        </p>
      ) : null}
    </UnsubscribeCard>
  );
}
